import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare, CheckCircle, XCircle, Settings, Send, RefreshCw,
  Zap, Phone, Globe, Key, Webhook, AlertTriangle, ChevronDown, ChevronUp,
  Users, Inbox, ExternalLink, RotateCcw, Activity, Eye, EyeOff, Plus,
  Wifi, WifiOff, Bot, Copy, Info, Trash2, ChevronRight, ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KAPSO_DOCS_URL = 'https://docs.kapso.ai/docs/introduction';
const KAPSO_TEAM_DOCS_URL = 'https://docs.kapso.ai/docs/platform/for-your-team';

const kapsoStatusError = (error) => error.response?.status === 401 &&
  error.response?.data?.code === 'ADMIN_AUTH_REQUIRED'
  ? { status: 'admin_auth_error', message: 'Sesión de Super Admin ausente o vencida. Inicia sesión de nuevo para comprobar Kapso.' }
  : { status: 'error', message: 'No se pudo verificar el estado' };

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    connected:      { label: 'Conectado',          color: 'text-green-400',  bg: 'bg-green-500/15 border-green-500/30',  icon: <Wifi size={12} /> },
    unverified:     { label: 'Sin verificar',      color: 'text-yellow-400', bg: 'bg-yellow-500/15 border-yellow-500/30', icon: <AlertTriangle size={12} /> },
    admin_auth_error: { label: 'Sesión administrativa requerida', color: 'text-red-400', bg: 'bg-red-500/15 border-red-500/30', icon: <XCircle size={12} /> },
    not_configured: { label: 'No configurado',     color: 'text-gray-400',   bg: 'bg-gray-500/15 border-gray-500/30',   icon: <WifiOff size={12} /> },
    auth_error:     { label: 'Error de API Key',   color: 'text-red-400',    bg: 'bg-red-500/15 border-red-500/30',     icon: <XCircle size={12} /> },
    error:          { label: 'Error de conexión',  color: 'text-red-400',    bg: 'bg-red-500/15 border-red-500/30',     icon: <XCircle size={12} /> },
    timeout:        { label: 'Timeout',            color: 'text-yellow-400', bg: 'bg-yellow-500/15 border-yellow-500/30', icon: <AlertTriangle size={12} /> },
  };
  const s = map[status] || map.not_configured;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${s.bg} ${s.color}`}>
      {s.icon}{s.label}
    </span>
  );
};

// ─── Config Section ───────────────────────────────────────────────────────────
function ConfigSection({ onSaved }) {
  const [form, setForm] = useState({
    api_url: 'https://api.kapso.ai/meta/whatsapp/v24.0',
    api_key: '',
    phone_number_id: '',
    whatsapp_number: '',
    webhook_url: '',
    auto_reply: true,
    is_active: true,
    linked_agent_id: 'main',
  });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const [detectedNumbers, setDetectedNumbers] = useState([]);
  const [showNumberPicker, setShowNumberPicker] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/kapso/config`),
      axios.get(`${API}/admin/ai-agents/`).catch(() => ({ data: [] })),
    ]).then(([konfig, agentsRes]) => {
      if (konfig.data.configured && konfig.data.config) {
        setForm(p => ({ ...p, ...konfig.data.config }));
      }
      setAgents(agentsRes.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const detectPhoneNumbers = async () => {
    if (!form.api_key || form.api_key.includes('...')) {
      toast.error('Guarda primero la configuración con tu API Key completa');
      return;
    }
    setDetecting(true);
    try {
      const r = await axios.get(`${API}/admin/kapso/phone-numbers`);
      const numbers = r.data?.phone_numbers || [];
      if (numbers.length === 0) {
        toast.info('No se encontraron números. Verifica tu API Key de Kapso.');
      } else {
        setDetectedNumbers(numbers);
        setShowNumberPicker(true);
        toast.success(`${numbers.length} número(s) detectado(s) en tu cuenta Kapso`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error al detectar números';
      toast.error(msg);
    } finally {
      setDetecting(false);
    }
  };

  const save = async () => {
    if (!form.api_key || !form.api_key.includes('...') && form.api_key.length < 8) {
      if (!form.api_key) { toast.error('La API Key es requerida'); return; }
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/kapso/config`, form);
      toast.success('Configuración de Kapso guardada');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-10">
      <RefreshCw className="w-5 h-5 text-red-400 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex gap-3">
        <Info size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-blue-300 text-sm font-medium">Integración Kapso WhatsApp</p>
          <p className="text-blue-400/70 text-xs mt-1">
            Kapso es el motor de mensajería WhatsApp. Configura tu API Key y número de WhatsApp para enviar y recibir mensajes desde OkBot.
          </p>
          <div className="flex gap-3 mt-2">
            <a href={KAPSO_DOCS_URL} target="_blank" rel="noreferrer" className="text-blue-400 text-xs flex items-center gap-1 hover:underline">
              <ExternalLink size={10} />API Docs
            </a>
            <a href={KAPSO_TEAM_DOCS_URL} target="_blank" rel="noreferrer" className="text-blue-400 text-xs flex items-center gap-1 hover:underline">
              <ExternalLink size={10} />For your team
            </a>
          </div>
        </div>
      </div>

      {/* Connection */}
      <div className="glass-panel rounded-xl p-5 space-y-4">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Key size={14} className="text-red-400" />Conexión API</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">URL Base de Kapso</label>
            <input value={form.api_url} onChange={e => set('api_url', e.target.value)}
              className="input-field w-full text-sm" placeholder="https://api.kapso.ai/meta/whatsapp/v24.0" />
            <p className="text-gray-600 text-xs mt-1">URL base oficial: <span className="text-gray-400 font-mono">https://api.kapso.ai/meta/whatsapp/v24.0</span></p>
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">API Key de Proyecto *</label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={form.api_key}
                onChange={e => set('api_key', e.target.value)}
                className="input-field w-full text-sm pr-10"
                placeholder="tu_api_key_de_kapso"
              />
              <button onClick={() => setShowKey(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="text-gray-600 text-xs mt-1">Kapso → Sidebar → <strong>API Keys</strong> → genera una llave de proyecto</p>
          </div>
        </div>
      </div>

      {/* WhatsApp Number — Kapso Personal Agent API */}
      <div className="glass-panel rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Phone size={14} className="text-green-400" />Número WhatsApp (Personal Agent)</h3>
          <a href="https://docs.kapso.ai/docs/whatsapp/personal-agent" target="_blank" rel="noreferrer" className="text-blue-400 text-xs flex items-center gap-1 hover:underline">
            <ExternalLink size={10} />Docs
          </a>
        </div>

        {/* How-to info banner */}
        <div className="bg-green-500/8 border border-green-500/20 rounded-xl p-3 text-xs text-green-400/80 space-y-1">
          <p className="font-medium text-green-300">¿Cómo configurar?</p>
          <ol className="list-decimal ml-4 space-y-1 text-green-400/70">
            <li>Ve a <strong>Kapso → Sidebar → API Keys</strong> y genera una llave de proyecto</li>
            <li>Copia el <strong>Phone Number ID</strong> de tu número — es el ID que ves en Kapso (también lo puedes autodetectar con el botón de abajo)</li>
            <li>El webhook de OkBot se autoconfigura al guardar</li>
          </ol>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">Número WhatsApp (display)</label>
            <input value={form.whatsapp_number} onChange={e => set('whatsapp_number', e.target.value)}
              className="input-field w-full text-sm" placeholder="+1 318-659-5786" />
            <p className="text-gray-600 text-xs mt-1">El número de teléfono visible (para mostrar al usuario)</p>
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">
              Phone Number ID de Kapso <span className="text-green-400">*</span>
            </label>
            <div className="flex gap-2">
              <input
                value={form.phone_number_id}
                onChange={e => { set('phone_number_id', e.target.value); setShowNumberPicker(false); }}
                className="input-field flex-1 text-sm font-mono"
                placeholder="Ej: 1081449345045353"
              />
              <button
                onClick={detectPhoneNumbers}
                disabled={detecting}
                title="Detectar números automáticamente desde Kapso API"
                className="px-3 py-2 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 rounded-lg text-green-400 text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {detecting ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                {detecting ? 'Buscando…' : 'Autodetectar'}
              </button>
            </div>

            {/* Phone number picker dropdown */}
            {showNumberPicker && detectedNumbers.length > 0 && (
              <div className="mt-2 bg-gray-900 border border-white/10 rounded-lg overflow-hidden shadow-xl z-10 relative">
                <p className="text-gray-500 text-xs px-3 py-2 border-b border-white/5">Selecciona tu número de WhatsApp:</p>
                {detectedNumbers.map(pn => (
                  <button
                    key={pn.id}
                    onClick={() => {
                      set('phone_number_id', pn.id);
                      if (pn.number) set('whatsapp_number', pn.number);
                      setShowNumberPicker(false);
                      toast.success(`Número seleccionado: ${pn.number || pn.name} (ID: ${pn.id})`);
                    }}
                    className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-xs font-medium">{pn.number || pn.name}</p>
                        <p className="text-gray-500 text-xs">{pn.name}{pn.status ? ` · ${pn.status}` : ''}</p>
                      </div>
                      <span className="text-gray-400 text-xs bg-white/5 rounded px-2 py-0.5 font-mono">{pn.id}</span>
                    </div>
                  </button>
                ))}
                <button onClick={() => setShowNumberPicker(false)} className="w-full text-center py-2 text-gray-600 text-xs hover:text-gray-400">Cancelar</button>
              </div>
            )}

            <p className="text-gray-600 text-xs mt-1.5">
              El ID del número de tu cuenta Kapso. Usa "Autodetectar" o cópialo de Kapso CLI: <code className="text-gray-500">kapso whatsapp numbers list</code>
            </p>
          </div>
        </div>
      </div>

      {/* Auto-reply toggle */}
      <div className="glass-panel rounded-xl p-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => set('auto_reply', !form.auto_reply)}
            className={`relative w-11 h-6 rounded-full transition-colors ${form.auto_reply ? 'bg-red-500' : 'bg-gray-700'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.auto_reply ? 'translate-x-5' : ''}`} />
          </button>
          <div>
            <p className="text-white text-sm">Respuesta automática activada</p>
            <p className="text-gray-500 text-xs">OkBot responde automáticamente a cada mensaje entrante de WhatsApp</p>
          </div>
        </div>
      </div>

      {/* Webhook */}
      <div className="glass-panel rounded-xl p-5 space-y-4">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Webhook size={14} className="text-orange-400" />Webhook (mensajes entrantes)</h3>
        <div>
          <label className="block text-gray-400 text-xs mb-1.5">URL del Webhook de OkBot</label>
          <div className="flex gap-2">
            <input
              value={form.webhook_url || `${process.env.REACT_APP_BACKEND_URL}/api/admin/kapso/webhook/receive`}
              onChange={e => set('webhook_url', e.target.value)}
              className="input-field flex-1 text-sm"
              placeholder="https://okbot.okgo.mx/api/admin/kapso/webhook/receive"
            />
            <button
              onClick={() => {
                const url = form.webhook_url || `${process.env.REACT_APP_BACKEND_URL}/api/admin/kapso/webhook/receive`;
                navigator.clipboard.writeText(url);
                toast.success('URL copiada');
              }}
              className="p-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-gray-400"
            >
              <Copy size={14} />
            </button>
          </div>
          <p className="text-gray-600 text-xs mt-1">
            Copia esta URL y configúrala en <strong>Kapso → Settings → Webhooks → Add Webhook</strong>. 
            Evento: <code className="text-gray-500">whatsapp.message.received</code>
          </p>
        </div>
      </div>

      {/* Linked AI Agent */}
      <div className="glass-panel rounded-xl p-5 space-y-4">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Bot size={14} className="text-red-400" />Agente IA Vinculado</h3>
        <div>
          <label className="block text-gray-400 text-xs mb-1.5">Agente que responde a WhatsApp</label>
          <select
            value={form.linked_agent_id || ''}
            onChange={e => set('linked_agent_id', e.target.value)}
            className="input-field w-full text-sm"
          >
            <option value="">-- Selecciona un agente --</option>
            <option value="main">🤖 OkBot Jarvis (Orquestador principal) — delega a agentes expertos</option>
            <option disabled>───────────────────────</option>
            {agents.filter(a => a.is_active).map(a => (
              <option key={a.id} value={a.id}>{a.name} ({a.category || 'general'})</option>
            ))}
          </select>
          <p className="text-gray-600 text-xs mt-1">
            <strong className="text-amber-300">OkBot Principal</strong> recibe cada mensaje y decide qué agente experto debe atenderlo (Mercader, Secretario, Financiero, Doctor, Agente, Melómano, Profesor, Cinéfilo). Recomendado.
          </p>
        </div>
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 flex gap-2">
          <CheckCircle size={13} className="text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-green-300 text-xs">
            El agente responde automáticamente a cada mensaje entrante de WhatsApp usando su configuración de sistema prompt, skills y modelos IA.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2">
          {saving ? <RefreshCw size={15} className="animate-spin" /> : <CheckCircle size={15} />}
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </div>
    </div>
  );
}

// ─── Status Section ────────────────────────────────────────────────────────────
function StatusSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/kapso/status`);
      setStatus(r.data);
    } catch (error) { setStatus(kapsoStatusError(error)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const statusColor = status?.status === 'connected'
    ? 'text-green-400'
    : status?.status === 'not_configured'
    ? 'text-gray-400'
    : 'text-red-400';

  return (
    <div className="space-y-4">
      <div className="glass-panel rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Activity size={14} className="text-red-400" />Estado de Conexión</h3>
          <button onClick={checkStatus} disabled={loading} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
        {loading ? (
          <div className="flex items-center gap-3 py-4">
            <RefreshCw size={16} className="text-red-400 animate-spin" />
            <span className="text-gray-400 text-sm">Verificando conexión...</span>
          </div>
        ) : status ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <StatusBadge status={status.status} />
              {status.account && <span className="text-gray-300 text-sm">Cuenta: <strong>{status.account}</strong></span>}
              {status.endpoint_used && <span className="text-gray-600 text-xs">· ruta: {status.endpoint_used}</span>}
            </div>
            <p className={`text-sm ${statusColor}`}>{status.message}</p>
            {status.detail && status.status !== 'connected' && (
              <details className="mt-1">
                <summary className="text-gray-600 text-xs cursor-pointer hover:text-gray-400 select-none">Ver detalle del error</summary>
                <pre className="text-gray-500 text-xs mt-1 bg-black/30 rounded p-2 overflow-auto max-h-24 whitespace-pre-wrap">{status.detail}</pre>
              </details>
            )}
            {status.whatsapp_number && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Phone size={13} className="text-green-400" />
                Número: <strong className="text-white">{status.whatsapp_number}</strong>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: MessageSquare, label: 'Motor WhatsApp', value: status?.status === 'connected' ? 'Activo' : 'Inactivo', color: 'text-green-400' },
          { icon: Bot, label: 'Auto-respuesta', value: 'OkBot IA', color: 'text-red-400' },
          { icon: Globe, label: 'API Kapso', value: 'v1', color: 'text-blue-400' },
        ].map((s, i) => (
          <div key={i} className="glass-panel rounded-xl p-4 flex flex-col gap-2">
            <s.icon size={16} className={s.color} />
            <p className={`text-sm font-semibold ${s.color}`}>{s.value}</p>
            <p className="text-gray-500 text-xs">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Team Config Section ──────────────────────────────────────────────────────
function TeamSection() {
  const [form, setForm] = useState({
    team_name: 'OkBot Team',
    inbox_id: '',
    description: 'Equipo de atención al cliente con OkBot IA',
    auto_assignment: true,
    max_conversations_per_agent: 10,
  });
  const [saving, setSaving] = useState(false);
  const [inboxes, setInboxes] = useState([]);
  const [loadingInboxes, setLoadingInboxes] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/kapso/team/config`).then(r => {
      if (r.data && Object.keys(r.data).length) setForm(p => ({ ...p, ...r.data }));
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const loadInboxes = async () => {
    setLoadingInboxes(true);
    try {
      const r = await axios.get(`${API}/admin/kapso/inboxes`);
      const list = r.data?.payload || r.data?.data || r.data || [];
      setInboxes(Array.isArray(list) ? list : []);
    } catch {
      toast.error('No se pudieron cargar los inboxes. Verifica tu conexión con Kapso.');
    } finally { setLoadingInboxes(false); }
  };

  const save = async () => {
    if (!form.team_name) { toast.error('El nombre del equipo es requerido'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/kapso/team/configure`, form);
      toast.success('Configuración del equipo guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 flex gap-3">
        <Users size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-purple-300 text-sm font-medium">Kapso For Your Team</p>
          <p className="text-purple-400/70 text-xs mt-1">
            La configuración "For Your Team" te permite crear un equipo de trabajo en Kapso y vincular el inbox de WhatsApp para que OkBot gestione y responda las conversaciones automáticamente.
          </p>
          <a href={KAPSO_TEAM_DOCS_URL} target="_blank" rel="noreferrer" className="text-purple-400 text-xs flex items-center gap-1 mt-1 hover:underline">
            <ExternalLink size={10} />Ver documentación completa
          </a>
        </div>
      </div>

      <div className="glass-panel rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Inbox size={14} className="text-purple-400" />Configuración del Equipo</h3>
          <button onClick={loadInboxes} disabled={loadingInboxes} className="text-xs text-purple-400 hover:underline flex items-center gap-1">
            <RefreshCw size={11} className={loadingInboxes ? 'animate-spin' : ''} />Cargar Inboxes
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">Nombre del Equipo</label>
            <input value={form.team_name} onChange={e => set('team_name', e.target.value)}
              className="input-field w-full text-sm" placeholder="OkBot WhatsApp Team" />
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">Inbox ID (WhatsApp)</label>
            {inboxes.length > 0 ? (
              <select value={form.inbox_id} onChange={e => set('inbox_id', e.target.value)} className="input-field w-full text-sm">
                <option value="">Selecciona un inbox</option>
                {inboxes.map(b => (
                  <option key={b.id} value={b.id}>{b.name} ({b.channel_type})</option>
                ))}
              </select>
            ) : (
              <input value={form.inbox_id} onChange={e => set('inbox_id', e.target.value)}
                className="input-field w-full text-sm" placeholder="ID del inbox de WhatsApp" />
            )}
          </div>
        </div>

        <div>
          <label className="block text-gray-400 text-xs mb-1.5">Descripción del equipo</label>
          <input value={form.description} onChange={e => set('description', e.target.value)}
            className="input-field w-full text-sm" placeholder="Equipo de atención al cliente" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => set('auto_assignment', !form.auto_assignment)}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.auto_assignment ? 'bg-purple-500' : 'bg-gray-700'}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.auto_assignment ? 'translate-x-5' : ''}`} />
            </button>
            <div>
              <p className="text-white text-sm">Asignación automática</p>
              <p className="text-gray-500 text-xs">Asigna conversaciones automáticamente</p>
            </div>
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">Máx. conversaciones por agente</label>
            <input type="number" min={1} max={100} value={form.max_conversations_per_agent}
              onChange={e => set('max_conversations_per_agent', parseInt(e.target.value) || 10)}
              className="input-field w-full text-sm" />
          </div>
        </div>

        <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2">
          {saving ? <RefreshCw size={15} className="animate-spin" /> : <CheckCircle size={15} />}
          {saving ? 'Guardando...' : 'Guardar Configuración del Equipo'}
        </button>
      </div>
    </div>
  );
}

// ─── Test Message Section ─────────────────────────────────────────────────────
function TestSection() {
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('Hola, este es un mensaje de prueba desde OkBot 🤖');
  const [sending, setSending] = useState(false);

  // Webhook simulation
  const [simPhone, setSimPhone] = useState('+521234567890');
  const [simMessage, setSimMessage] = useState('Hola, ¿en qué me puedes ayudar?');
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);

  const [sendError, setSendError] = useState(null);

  const send = async () => {
    if (!to.trim() || !message.trim()) { toast.error('Número y mensaje son requeridos'); return; }
    setSending(true);
    setSendError(null);
    try {
      await axios.post(`${API}/admin/kapso/message/send`, { to: to.trim(), message: message.trim() });
      toast.success('Mensaje enviado exitosamente');
      setSendError(null);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error al enviar mensaje';
      const is24h = detail.toLowerCase().includes('24') || detail.toLowerCase().includes('template') || detail.toLowerCase().includes('window');
      if (is24h) {
        setSendError({
          type: 'window',
          msg: 'Restricción de WhatsApp Business API: solo puedes enviar mensajes a usuarios que te hayan escrito en las últimas 24 horas. El destinatario debe enviarte un mensaje primero para abrir la conversación.',
        });
      } else {
        setSendError({ type: 'error', msg: detail });
        toast.error(detail);
      }
    } finally { setSending(false); }
  };

  const simulateWebhook = async () => {
    if (!simPhone.trim() || !simMessage.trim()) { toast.error('Número y mensaje requeridos'); return; }
    setSimulating(true);
    setSimResult(null);
    try {
      const r = await axios.post(`${API}/admin/kapso/webhook/test`, {
        phone: simPhone.trim(),
        message: simMessage.trim(),
      });
      setSimResult(r.data);
      toast.success('Simulación completada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en simulación');
    } finally { setSimulating(false); }
  };

  return (
    <div className="space-y-5">
      {/* Simulate Incoming Message */}
      <div className="glass-panel rounded-xl p-5 space-y-4">
        <div>
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <Bot size={14} className="text-green-400" />Simular mensaje entrante de WhatsApp
          </h3>
          <p className="text-gray-500 text-xs mt-1">
            Prueba cómo responde el agente IA sin necesidad de un número real de WhatsApp.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">Número (simulado)</label>
            <input value={simPhone} onChange={e => setSimPhone(e.target.value)}
              className="input-field w-full text-sm" placeholder="+521234567890" />
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">Mensaje del usuario</label>
            <input value={simMessage} onChange={e => setSimMessage(e.target.value)}
              className="input-field w-full text-sm" placeholder="¿Cuáles son sus horarios?" />
          </div>
        </div>
        <button onClick={simulateWebhook} disabled={simulating} className="btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700">
          {simulating ? <RefreshCw size={15} className="animate-spin" /> : <Zap size={15} />}
          {simulating ? 'Procesando...' : 'Simular & Ver Respuesta del Agente'}
        </button>
        {simResult && (() => {
          const delegateMatch = simResult.reply
            ? simResult.reply.match(/^([\s\S]*?)\n\n🤖 \*(.+?) dice:\*\n([\s\S]*)$/)
            : null;
          return (
            <div className="space-y-3 mt-2">
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <p className="text-gray-400 text-xs font-medium mb-1">📱 Mensaje del usuario:</p>
                <p className="text-white text-sm">{simResult.message}</p>
              </div>
              {delegateMatch ? (
                <div className="space-y-2">
                  {delegateMatch[1].trim() && (
                    <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
                      <p className="text-gray-400 text-xs font-medium mb-1 flex items-center gap-1.5">
                        <Bot size={11} className="text-green-400" />Agente principal:
                      </p>
                      <p className="text-white text-sm whitespace-pre-wrap">{delegateMatch[1].trim()}</p>
                    </div>
                  )}
                  <div className="bg-blue-500/10 border border-blue-400/30 rounded-xl p-3">
                    <p className="text-gray-400 text-xs font-medium mb-1 flex items-center gap-1.5">
                      <Bot size={11} className="text-blue-400" />
                      <span className="text-blue-300">{delegateMatch[2]}</span>
                      <span className="ml-auto bg-blue-500/20 text-blue-300 text-xs px-2 py-0.5 rounded-full">delegado</span>
                    </p>
                    <p className="text-white text-sm whitespace-pre-wrap">{delegateMatch[3].trim()}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                  <p className="text-gray-400 text-xs font-medium mb-1">🤖 Respuesta del agente IA:</p>
                  <p className="text-white text-sm whitespace-pre-wrap">{simResult.reply}</p>
                </div>
              )}
              <p className="text-gray-600 text-xs">
                Estado: {simResult.status} · Agente: {simResult.agent_id?.slice(0, 8)}...
                {delegateMatch && <span className="ml-2 text-blue-400">· orquestación multi-agente activa</span>}
              </p>
            </div>
          );
        })()}
      </div>

      {/* Send Real WhatsApp Message */}
      <div className="glass-panel rounded-xl p-5 space-y-4">
        <div>
          <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Send size={14} className="text-red-400" />Enviar mensaje real vía Kapso</h3>
          <p className="text-gray-500 text-xs mt-1">Requiere Kapso configurado y conectado.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">Número de WhatsApp destino</label>
            <input value={to} onChange={e => setTo(e.target.value)}
              className="input-field w-full text-sm" placeholder="+52 55 1234 5678" />
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">Mensaje</label>
            <input value={message} onChange={e => setMessage(e.target.value)}
              className="input-field w-full text-sm" placeholder="Escribe un mensaje de prueba..." />
          </div>
        </div>
        <button onClick={send} disabled={sending} className="btn-primary flex items-center gap-2">
          {sending ? <RefreshCw size={15} className="animate-spin" /> : <Send size={15} />}
          {sending ? 'Enviando...' : 'Enviar Mensaje de Prueba'}
        </button>
        {sendError && (
          <div className={`rounded-xl p-4 flex items-start gap-3 ${sendError.type === 'window' ? 'bg-blue-500/10 border border-blue-500/25' : 'bg-red-500/10 border border-red-500/25'}`}>
            <div className={`text-sm ${sendError.type === 'window' ? 'text-blue-300' : 'text-red-400'}`}>
              {sendError.type === 'window' && <p className="font-semibold mb-1">ℹ️ Ventana de 24 horas de WhatsApp Business API</p>}
              <p className="text-xs leading-relaxed">{sendError.msg}</p>
              {sendError.type === 'window' && (
                <p className="text-xs mt-2 text-blue-200 opacity-80">
                  <strong>Solución:</strong> Pide al destinatario que te escriba primero en WhatsApp. Una vez que te escriba, podrás responderle durante las siguientes 24 horas.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Logs Section ─────────────────────────────────────────────────────────────
function LogsSection() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/kapso/logs?limit=30`);
      setLogs(r.data.logs || []);
    } catch { toast.error('Error al cargar logs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Activity size={14} className="text-red-400" />Eventos Recientes</h3>
        <button onClick={loadLogs} disabled={loading} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw size={18} className="text-red-400 animate-spin" /></div>
      ) : logs.length === 0 ? (
        <div className="glass-panel rounded-xl p-8 text-center">
          <Activity size={28} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Sin eventos recientes</p>
          <p className="text-gray-600 text-xs mt-1">Los mensajes de WhatsApp aparecerán aquí</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log, i) => {
            const isProcessed = log.processed;
            const isError = !!log.error;
            const isSkipped = !!log.skip_reason;
            return (
              <div key={i} className={`glass-panel rounded-xl p-3 flex items-start gap-3 ${isError ? 'border border-red-500/20' : isProcessed ? 'border border-green-500/10' : ''}`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isError ? 'bg-red-500/20' : isProcessed ? 'bg-green-500/20' : 'bg-white/10'
                }`}>
                  {isError
                    ? <XCircle size={13} className="text-red-400" />
                    : isProcessed
                    ? <CheckCircle size={13} className="text-green-400" />
                    : <Activity size={13} className="text-gray-400" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white text-xs font-medium">{log.event_type || 'evento'}</p>
                    {log.phone && <span className="text-green-400 text-xs">{log.phone}</span>}
                    {isProcessed && <span className="text-green-400 text-xs">✓ respondido</span>}
                    {isSkipped && <span className="text-gray-500 text-xs">{log.skip_reason}</span>}
                    {isError && <span className="text-red-400 text-xs">error</span>}
                  </div>
                  <p className="text-gray-600 text-xs mt-0.5">{log.received_at}</p>
                  {log.message_preview && (
                    <p className="text-gray-400 text-xs mt-1 truncate">👤 {log.message_preview}</p>
                  )}
                  {log.reply_preview && (
                    <p className="text-gray-500 text-xs mt-0.5 truncate">🤖 {log.reply_preview}</p>
                  )}
                  {log.error && (
                    <p className="text-red-400 text-xs mt-1 truncate">{log.error}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Conversations Section ────────────────────────────────────────────────────
function ConversationsSection() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/kapso/whatsapp/conversations`);
      setThreads(r.data.conversations || []);
    } catch { toast.error('Error al cargar conversaciones'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = async (thread) => {
    setSelected(thread);
    setLoadingMsgs(true);
    try {
      const encoded = encodeURIComponent(thread.phone);
      const r = await axios.get(`${API}/admin/kapso/whatsapp/conversation/${encoded}`);
      setMessages(r.data.messages || []);
    } catch { toast.error('Error al cargar mensajes'); }
    finally { setLoadingMsgs(false); }
  };

  const clearThread = async (phone) => {
    if (!window.confirm(`¿Eliminar historial de ${phone}?`)) return;
    setClearing(true);
    try {
      const encoded = encodeURIComponent(phone);
      await axios.delete(`${API}/admin/kapso/whatsapp/conversation/${encoded}`);
      toast.success('Historial eliminado');
      setSelected(null);
      setMessages([]);
      loadThreads();
    } catch { toast.error('Error al eliminar'); }
    finally { setClearing(false); }
  };

  const fmtTime = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleString('es-MX', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ts; }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <MessageSquare size={14} className="text-green-400" />Conversaciones de WhatsApp
          </h3>
          <p className="text-gray-500 text-xs mt-0.5">{threads.length} hilos activos con historial</p>
        </div>
        <button onClick={loadThreads} disabled={loading} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {selected ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <button onClick={() => { setSelected(null); setMessages([]); }} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
              <ArrowLeft size={16} />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">{selected.sender_name || selected.phone}</p>
              <p className="text-gray-500 text-xs">{selected.phone} · {selected.total_messages} mensajes</p>
            </div>
            <button onClick={() => clearThread(selected.phone)} disabled={clearing}
              className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
              <Trash2 size={14} />
            </button>
          </div>

          <div className="glass-panel rounded-xl p-4 space-y-3 max-h-[500px] overflow-y-auto">
            {loadingMsgs ? (
              <div className="flex justify-center py-8"><RefreshCw size={18} className="text-green-400 animate-spin" /></div>
            ) : messages.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-6">Sin mensajes</p>
            ) : messages.map((m, i) => {
              const delegateMatch = m.role === 'assistant' && m.content
                ? m.content.match(/^([\s\S]*?)\n\n🤖 \*(.+?) dice:\*\n([\s\S]*)$/)
                : null;
              return (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-start' : 'justify-end'}`}>
                  {delegateMatch ? (
                    <div className="max-w-[85%] space-y-2">
                      {/* Main agent intro */}
                      {delegateMatch[1].trim() && (
                        <div className="bg-green-500/20 text-green-100 border border-green-500/20 rounded-xl px-3 py-2 text-sm">
                          <p className="whitespace-pre-wrap">{delegateMatch[1].trim()}</p>
                        </div>
                      )}
                      {/* Specialist agent reply */}
                      <div className="bg-blue-500/15 text-blue-100 border border-blue-500/25 rounded-xl px-3 py-2 text-sm">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <div className="w-4 h-4 rounded-full bg-blue-500/40 flex items-center justify-center">
                            <Bot size={9} className="text-blue-300" />
                          </div>
                          <span className="text-blue-300 text-xs font-semibold">{delegateMatch[2]}</span>
                          <span className="text-blue-500/60 text-xs ml-auto">delegado</span>
                        </div>
                        <p className="whitespace-pre-wrap">{delegateMatch[3].trim()}</p>
                        <p className="text-xs opacity-40 mt-1 text-right">{fmtTime(m.created_at)}</p>
                      </div>
                    </div>
                  ) : (
                    <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                      m.role === 'user'
                        ? 'bg-white/10 text-gray-200'
                        : 'bg-green-500/20 text-green-100 border border-green-500/20'
                    }`}>
                      <p className="whitespace-pre-wrap">{m.content}</p>
                      <p className="text-xs opacity-50 mt-1 text-right">{fmtTime(m.created_at)}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-10"><RefreshCw size={20} className="text-green-400 animate-spin" /></div>
      ) : threads.length === 0 ? (
        <div className="glass-panel rounded-xl p-10 text-center">
          <MessageSquare size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Sin conversaciones de WhatsApp</p>
          <p className="text-gray-600 text-xs mt-1">Cuando un usuario escriba por WhatsApp, el historial aparecerá aquí</p>
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map((t, i) => (
            <button key={i} onClick={() => openThread(t)}
              className="w-full glass-panel rounded-xl p-4 flex items-center gap-3 text-left hover:bg-white/5 transition-colors">
              <div className="w-10 h-10 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center flex-shrink-0">
                <Phone size={16} className="text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{t.sender_name || t.phone}</p>
                <p className="text-gray-500 text-xs truncate">{t.phone}</p>
                <p className="text-gray-400 text-xs truncate mt-0.5">
                  {t.last_role === 'assistant' ? '🤖 ' : '👤 '}{t.last_message}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <p className="text-gray-600 text-xs">{fmtTime(t.last_at)}</p>
                <span className="text-xs bg-white/10 px-2 py-0.5 rounded-full text-gray-400">{t.total_messages} msgs</span>
              </div>
              <ChevronRight size={14} className="text-gray-600 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────
const KapsoServerPanel = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [statusData, setStatusData] = useState(null);

  const refreshStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/kapso/status`);
      setStatusData(r.data);
    } catch (error) { setStatusData(kapsoStatusError(error)); }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const tabs = [
    { id: 'config', label: 'Configuración', icon: Settings },
    { id: 'team', label: 'For Your Team', icon: Users },
    { id: 'conversations', label: 'Conversaciones', icon: MessageSquare },
    { id: 'status', label: 'Estado', icon: Activity },
    { id: 'test', label: 'Probar', icon: Send },
    { id: 'logs', label: 'Eventos', icon: Zap },
  ];

  return (
    <div>
      {/* Jelou active banner */}
      <div className="mb-5 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-3">
        <Zap size={16} className="text-yellow-400 shrink-0 mt-0.5" />
        <div className="text-xs text-yellow-200">
          <strong className="text-yellow-300">Jelou está activo como proveedor principal de WhatsApp.</strong>
          {' '}Los mensajes de WhatsApp ahora llegan a OkBot a través del Brain HTTP de Jelou
          (<code className="bg-black/30 px-1 rounded">/api/jelou/webhook/brain</code>).
          Esta pantalla (Kapso) queda como proveedor de respaldo. Para configurar el número WA y el Brain Token,
          ve a <strong>Admin → Jelou Integration</strong>.
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-600/10 border border-green-500/20 flex items-center justify-center">
            <MessageSquare size={22} className="text-green-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              Kapso Server
              <span className="text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30 px-2 py-0.5 rounded-full">Respaldo</span>
              {statusData?.status === 'connected' && (
                <span className="text-xs bg-green-500/20 text-green-400 border border-green-500/30 px-2 py-0.5 rounded-full">Conectado</span>
              )}
            </h2>
            <p className="text-gray-500 text-sm mt-0.5">Motor de mensajería WhatsApp Business — Kapso API (respaldo)</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={refreshStatus} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
            <RefreshCw size={16} />
          </button>
          <a href={KAPSO_DOCS_URL} target="_blank" rel="noreferrer"
            className="btn-secondary flex items-center gap-2 text-sm">
            <ExternalLink size={14} />Docs Kapso
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 bg-white/3 border border-white/5 rounded-2xl overflow-x-auto no-scrollbar">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                activeTab === t.id ? 'bg-green-500/20 text-green-300 shadow border border-green-500/30' : 'text-gray-500 hover:text-gray-300'
              }`}>
              <Icon size={14} />{t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === 'config' && <ConfigSection onSaved={refreshStatus} />}
      {activeTab === 'team' && <TeamSection />}
      {activeTab === 'conversations' && <ConversationsSection />}
      {activeTab === 'status' && <StatusSection />}
      {activeTab === 'test' && (
        <div className="space-y-4">
          <TestSection />
          <div className="glass-panel rounded-xl p-4 flex gap-3">
            <Info size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-blue-400/80 text-xs">El número de destino debe estar guardado como contacto en WhatsApp y tu cuenta Kapso debe tener el inbox configurado correctamente.</p>
          </div>
        </div>
      )}
      {activeTab === 'logs' && <LogsSection />}
    </div>
  );
};

export default KapsoServerPanel;
